<template>
  <div class="scores-sorting">
    <draggable
        v-model="localPlan"
        :group="{ name: 'scores-sorting' }"
        handle=".drag-handle"
        :animation="150"
        :force-fallback="true"
        ghost-class="is-ghost"
        chosen-class="is-chosen"
        drag-class="is-dragging"
        @start="onDragStart"
        @end="onDragEnd"
        @change="emitPlan">

        <div v-for="(s, i) in localPlan"
            :key="`sort-${s.key || 'empty'}-${i}`"
            :class="{ 'mt-1': i > 0 }"
            class="mb-1 sort-level">
          <b-input-group>
            <template #prepend>
              <b-input-group-text
                  class="drag-handle cursor-move"
                  title="Drag to reorder">
                <b-icon-grip-vertical />
              </b-input-group-text>
            </template>

            <v-select
                :id="`sort-field-${_uid}-${i}`"
                v-model="s.key"
                :options="fields"
                label="text"
                :reduce="o => o.key"
                :select-on-tab="true"
                :dir="dir"
                append-to-body
                :calculate-position="withPopper"
                class="font-small-3 d-print-none table-select"
                @input="emitPlan"
            />

            <b-input-group-append>
              <b-button
                  :title="s.dir === 'asc' ? 'Ascending' : 'Descending'"
                  size="sm"
                  variant="outline-primary"
                  @click.stop.prevent="toggleDir(i)">
                <b-icon :icon="getIcon(s.key, s.dir)" />
              </b-button>

              <b-button
                  title="Remove level"
                  size="sm"
                  variant="outline-danger"
                  :disabled="localPlan.length === 1"
                  @click.stop.prevent="removeLevel(i)">
                <b-icon icon="trash" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
    </draggable>

    <b-button variant="outline-primary" block size="sm" @click="addLevel">
      <b-icon icon="plus" class="mr-50" /> Add Sort Level
    </b-button>

    <b-button variant="outline-secondary" block size="sm" class="mt-50" @click="reset">
      <b-icon icon="arrow-counterclockwise" class="mr-50" /> Reset
    </b-button>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import draggable from 'vuedraggable'

export default {
  name: 'ScoresSorting',
  components: { vSelect, draggable },
  props: {
    value: { type: Array, default: () => [] },
    defaultPlan: { type: Array, default: () => [] },
    fields: { type: Array, required: true },
    dir: { type: String, default: 'ltr' },
    withPopper: { type: Function, default: null },
  },
  data() {
    return {
      localPlan: this.clonePlan(this.value),
      isDragging: false,
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        // keep local in sync if parent overwrites plan (load settings / reset / etc.)
        this.localPlan = this.clonePlan(newVal)
      },
    },
  },
  methods: {
    getIcon(fieldKey, dir) {
      const d = dir === 'desc' ? 'desc' : 'asc'
      const field = this.fields.find((f) => String(f.key) === String(fieldKey))
      const t = field?.type ? String(field.type).toLowerCase() : 'string'

      if (t === 'number' || t === 'numeric' || t === 'int' || t === 'float') {
        return d === 'asc' ? 'sort-numeric-down' : 'sort-numeric-down-alt'
      }
      if (t === 'date' || t === 'datetime' || t === 'time') {
        return d === 'asc' ? 'sort-down' : 'sort-down-alt'
      }
      return d === 'asc' ? 'sort-alpha-down' : 'sort-alpha-down-alt'
    },

    clonePlan(plan) {
      return (plan || []).map((s) => ({
        key: s?.key ?? null,
        dir: s?.dir === 'desc' ? 'desc' : 'asc',
      }))
    },

    emitPlan() {
      // emit a *new array* so Vue 2 always notices
      const out = (this.localPlan || []).map((s) => ({ key: s.key, dir: s.dir }))
      this.$emit('input', out)
      this.$emit('change', out)
    },

    addLevel() {
      const firstKey = this.fields?.[0]?.key || 'student.name.last'
      this.localPlan.push({ key: firstKey, dir: 'asc' })
      this.emitPlan()
    },

    removeLevel(i) {
      if (this.localPlan.length <= 1) return
      this.localPlan.splice(i, 1)
      this.emitPlan()
    },

    toggleDir(i) {
      const row = this.localPlan[i]
      if (!row) return
      row.dir = row.dir === 'asc' ? 'desc' : 'asc'
      this.emitPlan()
    },

    reset() {
      const fallback = this.defaultPlan && this.defaultPlan.length
        ? this.defaultPlan
        : [
          { key: 'student.name.last', dir: 'asc' },
          { key: 'student.name.first', dir: 'asc' },
          { key: 'ranking.local', dir: 'asc' },
          { key: 'ranking.score', dir: 'desc' },
        ]
      this.localPlan = this.clonePlan(fallback)
      this.emitPlan()
    },

    onDragStart() {
      this.isDragging = true
    },
    onDragEnd() {
      this.isDragging = false
      // emitPlan already fires on @change, but @end is a safe backstop
      this.emitPlan()
    },
  },
}
</script>

<style scoped>
.cursor-move {
  cursor: move;
}

.sort-level.is-dragging {
  opacity: 0.65;
}

/* vuedraggable classes */
.is-ghost {
  opacity: 0.35;
}

.is-chosen {
  opacity: 0.8;
}

/* optional subtle animation on reorder */
.flip-list-move {
  transition: transform 150ms;
}
</style>
